import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { bookings } from '@repletoryreach/sdk-api';
import { Button, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert, DataTable, EmptyState, PageHeader, StatusBadge, TableShell } from '@repletoryreach/sdk-ui/data-display';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { HelpButton } from '../../../app/guides/HelpButton';
import { useT } from '../../../i18n/I18nProvider';
import type { Indexed } from '../../../lib/types';
import { useIsOrgAdmin } from '../../../app/components/RoleGate';
import { BOOKINGS_KEY, bookingErrorText, serviceColour, useBookingSettings, useOrgTimezone, useServices, useStaff } from '../lib/data';
import { StaffDrawer } from './StaffDrawer';
import { CalendarCell, CalendarPanel, useCalendarReturn, useCalendars } from './calendars';
import '../bookings.css';

export default function BookingStaffPage() {
  const t = useT();
  const nav = useNavigate();
  const qc = useQueryClient();
  const tz = useOrgTimezone();
  const canEdit = useIsOrgAdmin();
  const [editing, setEditing] = useState<bookings.Staff | 'new' | null>(null);
  const staffQ = useStaff();
  const servicesQ = useServices(false);
  const settingsQ = useBookingSettings();
  const calendars = useCalendars().data?.data;
  useCalendarReturn();
  const rows = (staffQ.data?.data ?? []) as Indexed<bookings.Staff>[];
  const services = servicesQ.data?.data ?? [];
  const limit = settingsQ.data?.data.limits?.booking_staff ?? null;
  const activeCount = rows.filter((s) => s.active).length;
  const atLimit = limit !== null && activeCount >= limit;

  const refresh = () => qc.invalidateQueries({ queryKey: [BOOKINGS_KEY] });
  const remove = useMutation({
    mutationFn: (s: bookings.Staff) => bookings.deleteStaff(s.id),
    onSuccess: (_r, s) => { void refresh(); notifySuccess(t('bookings.staff.toast.removed', { name: s.name }), t('bookings.staff.title')); },
    onError: (e) => notifyError(bookingErrorText(t, e), t('bookings.staff.title'))
  });

  function confirmRemove(s: bookings.Staff) {
    openConfirmModal({
      title: t('bookings.staff.remove.title', { name: s.name }),
      children: <Text role="bodySmall">{t('bookings.staff.remove.body')}</Text>,
      labels: { confirm: t('action.remove'), cancel: t('action.back') },
      confirmProps: { color: 'red' },
      onConfirm: () => remove.mutate(s)
    });
  }

  const servicesFor = (id: string) => services.filter((s) => s.staff_ids.includes(id)).map((s) => s.name);

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('bookings.staff.title')}
        description={t('bookings.staff.description')}
        withDivider={false}
        actions={(
          <Group gap="sm">
            <HelpButton guide="bookings" />
            {canEdit && <Button size="sm" data-guide="bookings-staff-add" disabled={atLimit} onClick={() => setEditing('new')}>{t('bookings.staff.new')}</Button>}
          </Group>
        )}
      />

      {!canEdit && <Alert color="info">{t('bookings.readOnly')}</Alert>}
      {canEdit && atLimit && (
        <Alert color="warning">
          <Group gap="sm" justify="space-between" wrap="wrap">
            <span>{t('bookings.staff.limitReached', { limit })}</span>
            <Button size="xs" variant="default" onClick={() => nav('/billing')}>{t('bookings.limit.seePlans')}</Button>
          </Group>
        </Alert>
      )}

      <CalendarPanel canEdit={canEdit} />

      <TableShell
        data-guide="bookings-staff-list"
        totalLabel={limit !== null ? t('bookings.staff.countOfLimit', { count: activeCount, limit }) : undefined}
        loading={staffQ.isPending}
        error={staffQ.error ? (
          <Alert color="error" role="alert">
            <Group gap="sm" justify="space-between" wrap="wrap">
              <span>{bookingErrorText(t, staffQ.error)}</span>
              <Button size="xs" variant="default" onClick={() => void staffQ.refetch()}>{t('action.retry')}</Button>
            </Group>
          </Alert>
        ) : undefined}
        isEmpty={!staffQ.isPending && rows.length === 0}
        empty={(
          <EmptyState
            title={t('bookings.staff.emptyTitle')}
            description={t('bookings.staff.emptyHint')}
            action={canEdit ? <Button size="sm" onClick={() => setEditing('new')}>{t('bookings.staff.addFirst')}</Button> : undefined}
          />
        )}
      >
        <DataTable<Indexed<bookings.Staff>>
          records={rows}
          idAccessor="id"
          onRowClick={(s) => setEditing(s)}
          columns={[
            {
              accessor: 'name',
              title: t('common.name'),
              render: (s) => (
                <Group gap={8} wrap="nowrap">
                  <span className="rr-bookings__swatch" style={{ background: serviceColour(s.colour) }} aria-hidden />
                  <Stack gap={0}>
                    <Text role="bodySmall" fw={600}>{s.name}</Text>
                    <Text role="caption" c="var(--rr-color-text-muted)">{s.email || s.phone || ''}</Text>
                  </Stack>
                </Group>
              )
            },
            {
              accessor: 'hours',
              title: t('bookings.staff.hours'),
              render: (s) => <Text role="caption">{s.hours ? t('bookings.staff.hours.own') : t('bookings.staff.hours.business')}</Text>
            },
            {
              accessor: 'services',
              title: t('bookings.services.title'),
              render: (s) => {
                const names = servicesFor(s.id);
                return names.length === 0
                  ? <Text role="caption" c="var(--rr-color-text-muted)">{t('common.none')}</Text>
                  : <Text role="bodySmall" lineClamp={1} title={names.join(', ')}>{names.join(', ')}</Text>;
              }
            },
            ...(calendars?.allowed || calendars?.connections.length ? [{
              accessor: 'calendar',
              title: t('bookings.calendar.column'),
              render: (s: Indexed<bookings.Staff>) => <CalendarCell overview={calendars} staffId={s.id} canEdit={canEdit} compact />
            }] : []),
            {
              accessor: 'active',
              title: t('common.status'),
              render: (s) => <StatusBadge status={s.active ? 'healthy' : 'inactive'}>{s.active ? t('bookings.staff.on') : t('bookings.staff.off')}</StatusBadge>
            },
            {
              accessor: 'actions',
              title: t('common.actions'),
              render: (s) => (
                <Group gap="xs" wrap="nowrap" onClick={(e) => e.stopPropagation()}>
                  <Button size="xs" variant="subtle" onClick={() => setEditing(s)}>{canEdit ? t('action.edit') : t('action.view')}</Button>
                  {canEdit && (
                    <Button size="xs" variant="subtle" color="red" loading={remove.isPending && remove.variables?.id === s.id} onClick={() => confirmRemove(s)}>
                      {t('action.remove')}
                    </Button>
                  )}
                </Group>
              )
            }
          ]}
        />
      </TableShell>

      <StaffDrawer target={editing} canEdit={canEdit} tz={tz} onClose={() => setEditing(null)} onSaved={() => void refresh()} />
    </Stack>
  );
}
